import { getFirestore, collection, query, where, getDocs, orderBy, limit as limitQuery } from 'firebase/firestore';
import { Product, isProductOptimized, getMarketplaceOptimizationRate } from '../../types/product';
import { dataApp } from '../../config/firebase';

const db = getFirestore(dataApp);
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const METRICS_CACHE_KEY = 'dashboardMetricsCache';
const PRODUCTS_CACHE_KEY = 'productsCache';

interface CacheData<T> {
  data: T;
  timestamp: number;
}

const loadCache = <T>(key: string): CacheData<T> | null => {
  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      console.error(`Error parsing ${key} cache:`, e);
      return null;
    }
  }
  return null;
};

const saveCache = <T>(key: string, data: T) => {
  try {
    const cacheData: CacheData<T> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (e) {
    console.error(`Error saving ${key} cache:`, e);
  }
};

export interface DashboardMetrics {
  totalProducts: number;
  optimizedProducts: number;
  optimizationRate: number;
  shopifyOptimizationRate: number;
  amazonOptimizationRate: number;
  googleOptimizationRate: number;
  recentProducts: Product[];
}

class DashboardService {
  private static instance: DashboardService;

  private constructor() {
    // Listen for product updates
    window.addEventListener('refreshProducts', () => {
      this.clearCache();
    });
  }

  clearCache() {
    console.log('Clearing dashboard caches');
    localStorage.removeItem(METRICS_CACHE_KEY);
    localStorage.removeItem(PRODUCTS_CACHE_KEY);
  }

  static getInstance(): DashboardService {
    if (!DashboardService.instance) {
      DashboardService.instance = new DashboardService();
    }
    return DashboardService.instance;
  }

  async getProducts(): Promise<Product[]> {
    try {
      // Check cache first
      const cache = loadCache<Product[]>(PRODUCTS_CACHE_KEY);
      const now = Date.now();
      if (cache && now - cache.timestamp < CACHE_TTL) {
        console.log('Using cached products');
        return cache.data;
      }

      console.log('Fetching fresh products data');
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        orderBy('timestamps.updated', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];

      // Save to cache
      saveCache(PRODUCTS_CACHE_KEY, products);
      return products;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw new Error('Failed to fetch products');
    }
  }

  async getRecentProducts(limitCount: number = 10): Promise<Product[]> {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        orderBy('timestamps.updated', 'desc'),
        limitQuery(limitCount)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];
    } catch (error) {
      console.error('Error fetching recent products:', error);
      throw new Error('Failed to fetch recent products');
    }
  }

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      // Check cache first
      const cache = loadCache<DashboardMetrics>(METRICS_CACHE_KEY);
      const now = Date.now();
      if (cache && now - cache.timestamp < CACHE_TTL) {
        console.log('Using cached dashboard metrics');
        return cache.data;
      }

      console.log('Fetching fresh dashboard metrics');
      const products = await this.getProducts();
      const recentProducts = await this.getRecentProducts();

      const totalProducts = products.length;
      const optimizedProducts = products.filter(isProductOptimized).length;
      const optimizationRate = totalProducts > 0 
        ? (optimizedProducts / totalProducts) * 100 
        : 0;

      // Calculate marketplace-specific rates
      const shopifyOptimizationRate = getMarketplaceOptimizationRate(products, 'shopify');
      const amazonOptimizationRate = getMarketplaceOptimizationRate(products, 'amazon');
      const googleOptimizationRate = getMarketplaceOptimizationRate(products, 'googleShopping');

      const metrics: DashboardMetrics = {
        totalProducts,
        optimizedProducts,
        optimizationRate,
        shopifyOptimizationRate,
        amazonOptimizationRate,
        googleOptimizationRate,
        recentProducts
      };

      // Save to cache
      saveCache(METRICS_CACHE_KEY, metrics);
      return metrics;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw new Error('Failed to fetch dashboard metrics');
    }
  }

  async getProductsByStatus(status: Product['status']): Promise<Product[]> {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('status', '==', status),
        orderBy('timestamps.updated', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];
    } catch (error) {
      console.error('Error fetching products by status:', error);
      throw new Error('Failed to fetch products by status');
    }
  }

  async getProductsByStore(storeName: string): Promise<Product[]> {
    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('storeName', '==', storeName),
        orderBy('timestamps.updated', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Product[];
    } catch (error) {
      console.error('Error fetching products by store:', error);
      throw new Error('Failed to fetch products by store');
    }
  }

  async getProductsByOptimizationStatus(isOptimized: boolean): Promise<Product[]> {
    try {
      const products = await this.getProducts();
      return products.filter(product => isProductOptimized(product) === isOptimized);
    } catch (error) {
      console.error('Error fetching products by optimization status:', error);
      throw new Error('Failed to fetch products by optimization status');
    }
  }
}

export const dashboardService = DashboardService.getInstance();
